import { Building } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import {
  UseCaseHero,
  UseCaseContext,
  UseCaseProblems,
  UseCaseSolution,
  UseCaseResults,
  UseCaseTestimonial,
  UseCaseCTA,
  RelatedUseCases,
} from "@/components/use-cases";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const ACCENT_COLOR = "#F97316";

const problems = [
  "Cout main d'oeuvre qui explosait sans explication",
  "Derapage de 5% = marge entierement effacee",
  "Impossible de savoir quel chantier consommait trop de ressources",
  "Excel avec 15 onglets et formules cassees",
];

const solutions = [
  {
    title: "Calcul auto cout/jour",
    description: "Chaque membre a son cout journalier reel (charges incluses)",
  },
  {
    title: "Jour Critique visible",
    description: "Date ou le chantier devient deficitaire",
  },
  {
    title: "Simulateur penalites",
    description: "Impact financier des retards avec penalites contractuelles",
  },
  {
    title: "Rapports PDF",
    description: "Export pour reunions de chantier et banque",
  },
];

const results = [
  {
    metric: "Marge moyenne",
    before: "12%",
    after: "18% (+6 pts)",
  },
  {
    metric: "Chantiers deficitaires detectes a temps",
    before: "0",
    after: "3",
  },
  {
    metric: "Temps suivi/chantier",
    before: "3h/sem",
    after: "1h/sem",
  },
];

const Macon = () => {
  usePageMeta({
    title: "Logiciel gestion chantier maçon — B8ild",
    description: "Gestion financière pour maçons et entreprises de maçonnerie. Suivez la rentabilité de vos chantiers gros œuvre en temps réel.",
    url: "https://b8ild.fr/use-cases/macon",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
      <LandingHeader />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Cas d'usage", item: "https://b8ild.fr/use-cases" },
          { "@type": "ListItem", position: 3, name: "Maçon", item: "https://b8ild.fr/use-cases/macon" },
        ],
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Logiciel gestion chantier maçon — B8ild",
        description: "Gestion financière pour maçons et entreprises de maçonnerie. Suivez la rentabilité de vos chantiers gros œuvre en temps réel.",
        url: "https://b8ild.fr/use-cases/macon",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
        inLanguage: "fr",
      }} />

      <UseCaseHero
        badge="Macon / Entreprise generale"
        title="Pierre gere 18 personnes"
        subtitle="et 10 chantiers. Sereinement."
        description="Decouvrez comment B8ild aide les entreprises de maconnerie a maitriser leurs couts de main d'oeuvre."
        icon={Building}
        accentColor={ACCENT_COLOR}
        personaInitials="PD"
        personaName="Pierre"
        personaRole="PME maconnerie/gros oeuvre, 18 personnes"
        personaCity="Toulouse · CA ~1,8M EUR/an"
      />

      <UseCaseContext
        context="Pierre dirige une entreprise de maconnerie depuis 25 ans. Avec 12 macons, 3 chefs d'equipe et 3 administratifs, il gere des chantiers de construction et renovation lourde. Le cout de main d'oeuvre represente 60% de ses depenses."
        accentColor={ACCENT_COLOR}
      />

      <UseCaseProblems
        problems={problems}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseSolution
        solutions={solutions}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseResults
        results={results}
        accentColor={ACCENT_COLOR}
        period="3 mois"
      />

      <UseCaseTestimonial
        quote="Le Jour Critique m'a sauve sur un chantier de renovation. J'ai vu qu'il me restait 8 jours de marge, j'ai reaffecte une equipe, et j'ai fini avec 14% de marge au lieu de -2%."
        name="Pierre"
        role="Dirigeant"
        city="Toulouse"
        initials="PD"
        accentColor={ACCENT_COLOR}
      />

      {/* Spécificités métier */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Vos chantiers, on les connaît
            </h2>
            <p className="text-white/60 leading-relaxed">
              Fondation, dalle béton, élévation murs, extension maison, surélévation, enduit façade — le gros œuvre, c'est des coûts de main d'œuvre qui représentent 60% du budget. Un dérapage de 5% et votre marge est à zéro. B8ild suit vos chantiers RE2020 et DTU maçonnerie avec le détail qu'il faut pour réagir avant que ça dérape.
            </p>
          </div>
        </div>
      </section>

      {/* Cas concret */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Cas concret : Extension 120m² et béton à +8%
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                Thomas a signé un devis pour une extension de 120m² à 95 000€ HT en janvier. Béton : 18 000€ prévus. Ferraillage : 6 500€. Main d'œuvre : 4 ouvriers × 45 jours × 180€/jour = 32 400€. Le chantier démarre en mars. Problème : le béton a augmenté de 8% depuis le devis. 18 000€ → 19 440€. Le ferraillage aussi : +5%. 6 500€ → 6 825€. Surcoût matériaux : 1 765€.
              </p>
              <p>
                En plus, la dalle nécessite un vide sanitaire non prévu au devis (sol argileux découvert au terrassement). 3 jours de travail supplémentaires : 4 × 180€ × 3 = 2 160€. La marge prévisionnelle de Thomas était de 16 000€ (16,8%). Avec les surcoûts : 12 075€ (12,7%). 4 points de marge perdus sans avoir rien vu venir. Le DTU 20.1 impose des fondations adaptées au sol — pas le choix.
              </p>
              <p>
                Imaginez : Thomas entre ses achats au fur et à mesure. Dès le 2e camion de béton, alerte rouge — dépassement de 8% sur le poste matériaux. Il négocie un avenant pour le vide sanitaire AVANT de le réaliser. Pas 3 semaines après, quand c'est trop tard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions métier */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions fréquentes — Maçons
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment gérer la hausse des prix des matériaux ?",
                  a: "Ajoutez une clause de révision de prix dans vos devis. En 2024-2025, le béton a varié de 5 à 12% par an. Le ferraillage : 8 à 15%. La clause permet de répercuter les hausses au-delà d'un seuil (par exemple +5%). Sans cette clause, vous absorbez la totalité. B8ild compare vos achats réels au devis initial et alerte dès le premier dépassement.",
                },
                {
                  q: "Quel coefficient de charges pour un maçon ?",
                  a: "Entre 1.55 et 1.75 selon la taille de l'entreprise. Le BTP a des cotisations spécifiques : congés intempéries (0,68%), caisse CIBTP (12,5% pour les congés payés), AT/MP élevé (3-6% pour le gros œuvre). Un ouvrier maçon N3 à 2 300€ brut coûte environ 3 750€/mois toutes charges comprises.",
                },
                {
                  q: "Comment calculer le prix au m² en maçonnerie ?",
                  a: "Comptez le déboursé sec (matériaux + main d'œuvre directe) au m². Exemple pour une extension RE2020 : fondations 150-250€/m², élévation murs 180-300€/m², dalle 80-150€/m². Ajoutez 15-20% de frais généraux et 15-20% de marge. Total estimatif : 600 à 1 200€/m² selon la complexité et la région.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <UseCaseCTA accentColor={ACCENT_COLOR} />

      <RelatedUseCases currentSlug="macon" />

      <LandingFooter />
    </div>
  );
};

export default Macon;
